import { useQuery, useMutation, useQueryClient, UseQueryOptions } from "@tanstack/react-query";
import axios from "../../../lib/axios";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import { IJobPhoto, IJobTimeLog, IJobChecklistResponse, IJobPart, IJobSignOff } from "../../../types/api/incident";

function jobUrl(template: string, id: number) {
  return template.replace(":id", String(id));
}

export const useJobPhotos = (jobId: number | null) =>
  useQuery<IJobPhoto[]>({
    queryKey: ["job-photos", jobId],
    queryFn: async () => {
      const res = await axios.get(jobUrl(API_ENDPOINTS.JOB_PHOTOS, jobId!));
      return res.data?.data ?? [];
    },
    enabled: !!jobId,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

export const useJobTimeLogs = (jobId: number | null) =>
  useQuery<IJobTimeLog[]>({
    queryKey: ["job-time-logs", jobId],
    queryFn: async () => {
      const res = await axios.get(jobUrl(API_ENDPOINTS.JOB_TIME_LOGS, jobId!));
      return res.data?.data ?? [];
    },
    enabled: !!jobId,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

export const useJobChecklistResponses = (jobId: number | null) =>
  useQuery<IJobChecklistResponse[]>({
    queryKey: ["job-checklist-responses", jobId],
    queryFn: async () => {
      const res = await axios.get(jobUrl(API_ENDPOINTS.JOB_CHECKLIST_RESPONSES, jobId!));
      return res.data?.data ?? [];
    },
    enabled: !!jobId,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

export const useJobParts = (jobId: number | null) =>
  useQuery<IJobPart[]>({
    queryKey: ["job-parts", jobId],
    queryFn: async () => {
      const res = await axios.get(jobUrl(API_ENDPOINTS.JOB_PARTS, jobId!));
      return res.data?.data ?? [];
    },
    enabled: !!jobId,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

export const useJobSignOff = (jobId: number | null) =>
  useQuery<IJobSignOff | null>({
    queryKey: ["job-sign-off", jobId],
    queryFn: async () => {
      const res = await axios.get(jobUrl(API_ENDPOINTS.JOB_SIGN_OFF, jobId!));
      return res.data?.data ?? null;
    },
    enabled: !!jobId,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

export const useSaveJobSignOff = ({ jobId, onSuccess, onError }: { jobId: number; onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { customer_name?: string; completion_notes?: string; email_sent?: boolean }) => {
      const res = await axios.post(jobUrl(API_ENDPOINTS.JOB_SIGN_OFF, jobId), data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job-sign-off", jobId] });
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.JOB_LIST] });
      onSuccess?.();
    },
    onError,
  });
};

// ── Clock In / Out ──────────────────────────────────────────────────────────

export const useJobClockIn = ({ onSuccess, onError }: { onSuccess?: (data: any) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ jobId }: { jobId: number }) => {
      const url = API_ENDPOINTS.JOB_CLOCK_IN.replace(":id", String(jobId));
      const res = await axios.post(url);
      return res.data;
    },
    onSuccess: (data, vars) => {
      qc.invalidateQueries({ queryKey: ["job-time-logs", vars.jobId] });
      onSuccess?.(data);
    },
    onError,
  });
};

export const useJobClockOut = ({ onSuccess, onError }: { onSuccess?: (data: any) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ jobId, logId }: { jobId: number; logId: number }) => {
      const url = API_ENDPOINTS.JOB_CLOCK_OUT.replace(":id", String(jobId)).replace(":logId", String(logId));
      const res = await axios.post(url);
      return res.data;
    },
    onSuccess: (data, vars) => {
      qc.invalidateQueries({ queryKey: ["job-time-logs", vars.jobId] });
      onSuccess?.(data);
    },
    onError,
  });
};

// ── Time Log Correction (office edit of clock-in/out times) ─────────────────

export const useJobTimeLogUpdate = ({ jobId, onSuccess, onError }: { jobId: number; onSuccess?: (data: any) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ logId, start_time, end_time, notes }: { logId: number; start_time?: string; end_time?: string; notes?: string }) => {
      const url = API_ENDPOINTS.JOB_TIME_LOG_UPDATE.replace(":id", String(jobId)).replace(":logId", String(logId));
      const res = await axios.put(url, { start_time, end_time, notes });
      return res.data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["job-time-logs", jobId] });
      onSuccess?.(data);
    },
    onError,
  });
};

// ── Job Notes ────────────────────────────────────────────────────────────────

export interface IJobNote {
  id: number;
  note: string;
  visibility: "internal" | "client";
  created_at: string;
  created_by_name: string | null;
}

export const useJobNotes = (jobId: number | null, visibility?: string) =>
  useQuery<IJobNote[]>({
    queryKey: ["job-notes", jobId, visibility],
    queryFn: async () => {
      const url = API_ENDPOINTS.JOB_NOTES.replace(":id", String(jobId));
      const res = await axios.get(url, { params: visibility && visibility !== "all" ? { visibility } : {} });
      return res.data?.data ?? [];
    },
    enabled: !!jobId,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

export const useJobAddNote = ({ jobId, onSuccess, onError }: { jobId: number; onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: { note: string; visibility: "internal" | "client" }) => {
      const url = API_ENDPOINTS.JOB_NOTES.replace(":id", String(jobId));
      const res = await axios.post(url, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job-notes", jobId] });
      onSuccess?.();
    },
    onError,
  });
};

export const useJobDeleteNote = ({ jobId, onSuccess, onError }: { jobId: number; onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ nid }: { nid: number }) => {
      const url = API_ENDPOINTS.JOB_NOTE_DELETE.replace(":id", String(jobId)).replace(":nid", String(nid));
      const res = await axios.delete(url);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job-notes", jobId] });
      onSuccess?.();
    },
    onError,
  });
};
